import type { RadarTarget, TargetClassification, TargetCategory } from '@/shared/types'

const CLASSES: TargetClassification[] = ['friend', 'civil', 'civil', 'civil', 'unknown', 'unknown', 'foe', 'military-friend']
const CATEGORIES: TargetCategory[] = ['aircraft', 'aircraft', 'aircraft', 'helicopter', 'drone', 'unidentified']

function rand(seed: number, min: number, max: number): number {
  const x = Math.sin(seed) * 10000
  return min + (x - Math.floor(x)) * (max - min)
}

function pad(n: number, w: number): string {
  return String(n).padStart(w, '0')
}

function generateTargets(): RadarTarget[] {
  const list: RadarTarget[] = []
  const now = Date.now()
  for (let i = 0; i < 80; i++) {
    const cls = CLASSES[i % CLASSES.length]
    const cat = CATEGORIES[i % CATEGORIES.length]
    const region = i % 3
    let baseLat = 17, baseLon = 107
    if (region === 0) { baseLat = 21; baseLon = 106.5 }
    else if (region === 1) { baseLat = 15; baseLon = 109 }
    else { baseLat = 10; baseLon = 107 }

    const lat = baseLat + rand(i * 7 + 1, -2.5, 2.5)
    const lon = baseLon + rand(i * 11 + 3, -2.5, 2.5)
    const alt = cat === 'helicopter' ? Math.round(rand(i * 13, 200, 3000)) : cat === 'drone' ? Math.round(rand(i * 17, 50, 2500)) : Math.round(rand(i * 19, 1500, 12000))
    const speed = cat === 'helicopter' ? Math.round(rand(i * 23, 80, 280)) : cat === 'drone' ? Math.round(rand(i * 29, 30, 180)) : Math.round(rand(i * 31, 400, 950))
    const heading = Math.round(rand(i * 37, 0, 360))
    const detectedAgo = Math.floor(rand(i * 41, 30, 3600))
    const detectedAt = new Date(now - detectedAgo * 1000).toISOString()
    const lastUpdate = new Date(now - rand(i * 43, 0, 30) * 1000).toISOString()

    const trackHistory = Array.from({ length: 8 }, (_, k) => {
      const dt = (8 - k) * 30
      return {
        lat: lat - (Math.cos((heading * Math.PI) / 180) * speed * dt) / 111000 / 60,
        lon: lon - (Math.sin((heading * Math.PI) / 180) * speed * dt) / 111000 / 60,
        alt: alt + rand(i + k, -200, 200),
        t: new Date(now - dt * 1000).toISOString(),
      }
    })

    let threatLevel: 0 | 1 | 2 | 3 = 0
    if (cls === 'foe') threatLevel = 3
    else if (cls === 'unknown') threatLevel = 2
    else if (cls === 'civil') threatLevel = 0
    else threatLevel = 1

    list.push({
      id: `tgt-${i + 1}`,
      trackId: `T${pad(1000 + i, 4)}`,
      classification: cls,
      category: cat,
      status: i < 60 ? 'tracking' : i < 70 ? 'identified' : i < 75 ? 'lost' : 'archived',
      detectedAt,
      lastUpdate,
      position: { lat, lon, alt },
      velocity: { speed, heading, verticalSpeed: Math.round(rand(i * 47, -10, 10)) },
      detectedBy: [`rd-${(i % 15) + 1}`, `rd-${((i + 5) % 15) + 1}`],
      threatLevel,
      trackHistory,
      notes: cls === 'foe' ? 'Hành trình bất thường, không phản hồi định danh' : cls === 'unknown' ? 'Đang chờ định danh' : undefined,
    })
  }
  return list
}

export const TARGETS: RadarTarget[] = generateTargets()
export const TARGETS_BY_ID: Record<string, RadarTarget> = Object.fromEntries(
  TARGETS.map((t) => [t.id, t]),
)

export const CLASSIFICATION_COLOR: Record<TargetClassification, string> = {
  friend: '#10b981',
  'military-friend': '#1e6091',
  civil: '#06b6d4',
  unknown: '#f59e0b',
  foe: '#ef4444',
}

export const CLASSIFICATION_LABEL: Record<TargetClassification, string> = {
  friend: 'Quân ta',
  'military-friend': 'Quân sự bạn',
  civil: 'Dân dụng',
  unknown: 'Chưa rõ',
  foe: 'Đối phương',
}

export const CATEGORY_LABEL: Record<TargetCategory, string> = {
  aircraft: 'Máy bay',
  helicopter: 'Trực thăng',
  drone: 'UAV / Flycam',
  missile: 'Tên lửa',
  unidentified: 'Không xác định',
}
